import {
  collectProjectDrawingContentManifest,
  projectFromPersistenceState,
  rememberProjectDrawingContentManifest
} from './project_content_registry.mjs?v=20260904f';
import {
  PROJECT_DOCUMENT_FILE_TYPE,
  PROJECT_DOCUMENT_SCHEMA_VERSION,
  validateProjectDocumentVersions
} from '../../plugin-contracts/system_data_contract.mjs';
import {
  migrateProjectSystemData,
  projectSystemDataNeedsMigration,
  validateProjectSystemData
} from './system_data_service.js';

const PROJECT_FILE_TYPE = PROJECT_DOCUMENT_FILE_TYPE;
const PROJECT_FILE_SCHEMA_VERSION = PROJECT_DOCUMENT_SCHEMA_VERSION;

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function cloneJsonValue(value) {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
}

function persistedProjectClone(project) {
  if (!isPlainObject(project)) throw new Error('Projektzustand fehlt oder ist ungültig.');
  const clone = cloneJsonValue(project);
  delete clone.inputDrafts;
  return clone;
}

function assignOptionalJsonValue(target, key, value) {
  if (value === undefined) return;
  target[key] = cloneJsonValue(value);
}

function prepareInactiveSystemData(document, detached = false) {
  const envelope = document.drawingSheets;
  if (!Array.isArray(envelope?.sheets)) return document;
  let changed = false;
  const sheets = envelope.sheets.map(sheet => {
    if (sheet?.id === envelope.activeSheetId || !isPlainObject(sheet?.state?.project)) return sheet;
    validateProjectSystemData(sheet.state.project);
    let state = sheet.state;
    if (Object.hasOwn(state.project, 'systemOrderData')) {
      const { systemOrderData: _orderData, ...project } = state.project;
      state = { ...state, project };
    }
    if (projectSystemDataNeedsMigration(state.project)) {
      const project = detached ? state.project : persistedProjectClone(state.project);
      rememberProjectDrawingContentManifest(project, state.drawingContent);
      migrateProjectSystemData(project);
      state = { ...state, project, drawingContent: collectProjectDrawingContentManifest(project) };
    }
    state = prepareInactiveSystemData(state, detached);
    if (state === sheet.state) return sheet;
    changed = true;
    return { ...sheet, state };
  });
  return changed ? { ...document, drawingSheets: { ...envelope, sheets } } : document;
}

// The callers attach already serialized sheets after createProjectDocument to
// avoid a second complete JSON copy. Complete their validation at that boundary.
function finalizeProjectDocument(document) {
  validateProjectDocumentVersions(document);
  return prepareInactiveSystemData(document, true);
}

function createProjectDocument(options = {}) {
  validateProjectSystemData(options.project);
  const drawingContent = collectProjectDrawingContentManifest(options.project);
  const project = persistedProjectClone(options.project);
  rememberProjectDrawingContentManifest(project, drawingContent);
  migrateProjectSystemData(project);
  const document = {
    project,
    drawingContent: collectProjectDrawingContentManifest(project)
  };
  assignOptionalJsonValue(document, 'fileType', options.fileType);
  assignOptionalJsonValue(document, 'schemaVersion', options.schemaVersion ?? PROJECT_FILE_SCHEMA_VERSION);
  assignOptionalJsonValue(document, 'savedAt', options.savedAt);
  assignOptionalJsonValue(document, 'camera2D', options.camera2D);
  assignOptionalJsonValue(document, 'activeCompanyId', options.activeCompanyId);
  assignOptionalJsonValue(document, 'activeCategoryId', options.activeCategoryId);
  assignOptionalJsonValue(document, 'activeSystemId', options.activeSystemId);
  assignOptionalJsonValue(document, 'drawingSheets', options.drawingSheets);
  return finalizeProjectDocument(document);
}

function parseProjectDocument(raw) {
  const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
  validateProjectDocumentVersions(parsed);
  if (isPlainObject(parsed) && Object.prototype.hasOwnProperty.call(parsed, 'project') && !isPlainObject(parsed.project)) {
    throw new Error('Gespeicherter Zustand enthält kein gültiges Projekt.');
  }
  const project = projectFromPersistenceState(parsed);
  if (!project) throw new Error('Gespeicherter Zustand enthält kein gültiges Projekt.');
  validateProjectSystemData(project);
  const hasEnvelope = isPlainObject(parsed?.project);
  const document = hasEnvelope ? { ...parsed } : { project };
  document.project = persistedProjectClone(project);
  if (hasEnvelope) rememberProjectDrawingContentManifest(document.project, parsed.drawingContent);
  migrateProjectSystemData(document.project);
  document.drawingContent = collectProjectDrawingContentManifest(document.project);
  return prepareInactiveSystemData(document);
}

export {
  PROJECT_FILE_SCHEMA_VERSION,
  PROJECT_FILE_TYPE,
  cloneJsonValue,
  createProjectDocument,
  finalizeProjectDocument,
  parseProjectDocument,
  persistedProjectClone
};
